const DEFAULT_DURATION = 520;
const TRANSITION_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

let activeCleanup = null;

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getAnimationTarget() {
  return document.querySelector('[data-cart-animation-target]');
}

function getContextRoot(root) {
  if (!(root instanceof HTMLElement)) {
    return document;
  }

  return (
    root.closest('product-quick-view') ||
    root.closest('.product-section') ||
    root
  );
}

function getSourceImage(root) {
  const contextRoot = getContextRoot(root);

  // Prefer the active slide of the main swiper (not thumbnails).
  const activeMainImage = contextRoot.querySelector(
    'product-images .js-product-main-swiper .swiper-slide-active img',
  );
  if (activeMainImage) {
    const rect = activeMainImage.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return activeMainImage;
  }

  // Fallback: first visible image in main swiper.
  const mainImages = contextRoot.querySelectorAll(
    'product-images .js-product-main-swiper .swiper-slide img',
  );
  for (const img of mainImages) {
    const rect = img.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return img;
  }

  return null;
}

function getOverlayHost(sourceImage) {
  const openDialog = sourceImage.closest('dialog[open]');
  if (openDialog instanceof HTMLDialogElement) {
    return openDialog;
  }

  return document.body;
}

function mapRectToHost(rect, host) {
  if (!(host instanceof HTMLElement) || host === document.body) {
    return rect;
  }

  const hostRect = host.getBoundingClientRect();
  return {
    left: rect.left - hostRect.left,
    top: rect.top - hostRect.top,
    width: rect.width,
    height: rect.height,
  };
}

function createAnimationShell(sourceImage, sourceRect, host) {
  const shell = document.createElement('div');
  shell.className = 'cart-add-transition';
  shell.setAttribute('aria-hidden', 'true');
  shell.style.position =
    host instanceof HTMLDialogElement ? 'absolute' : 'fixed';
  shell.style.left = `${sourceRect.left}px`;
  shell.style.top = `${sourceRect.top}px`;
  shell.style.width = `${sourceRect.width}px`;
  shell.style.height = `${sourceRect.height}px`;
  shell.style.pointerEvents = 'none';
  shell.style.overflow = 'hidden';
  shell.style.zIndex = '2147483647';
  shell.style.borderRadius =
    window.getComputedStyle(sourceImage).borderRadius || '0px';
  shell.style.transformOrigin = 'center center';
  shell.style.willChange = 'transform, opacity, border-radius';
  shell.style.boxShadow = '0 12px 32px rgba(0, 0, 0, 0.16)';

  const clone = sourceImage.cloneNode(true);
  clone.className = sourceImage.className;
  clone.style.width = '100%';
  clone.style.height = '100%';
  clone.style.display = 'block';
  clone.style.objectFit = 'cover';
  clone.removeAttribute('sizes');

  shell.appendChild(clone);
  return shell;
}

function cleanupActiveTransition() {
  if (typeof activeCleanup === 'function') {
    activeCleanup();
  }
  activeCleanup = null;
}

function animateWithCss(shell, keyframe) {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      shell.style.transition = `transform ${keyframe.duration}ms ${TRANSITION_EASING}, opacity ${keyframe.duration}ms ${TRANSITION_EASING}, border-radius ${keyframe.duration}ms ${TRANSITION_EASING}`;
      shell.style.transform = keyframe.transform;
      shell.style.opacity = keyframe.opacity;
      shell.style.borderRadius = keyframe.borderRadius;

      window.setTimeout(resolve, keyframe.duration);
    });
  });
}

function runAnimation(shell, keyframe) {
  if (typeof shell.animate === 'function') {
    const animation = shell.animate(
      [
        {
          transform: 'translate3d(0, 0, 0) scale(1, 1)',
          opacity: 1,
          borderRadius: shell.style.borderRadius,
        },
        {
          transform: keyframe.transform,
          opacity: keyframe.opacity,
          borderRadius: keyframe.borderRadius,
        },
      ],
      {
        duration: keyframe.duration,
        easing: TRANSITION_EASING,
        fill: 'forwards',
      },
    );

    return animation.finished.catch(() => undefined);
  }

  return animateWithCss(shell, keyframe);
}

function buildKeyframe(sourceRect, targetRect, duration) {
  const sourceCenterX = sourceRect.left + sourceRect.width / 2;
  const sourceCenterY = sourceRect.top + sourceRect.height / 2;
  const targetSize = Math.max(
    20,
    Math.min(targetRect.width, targetRect.height) * 0.72,
  );
  const targetCenterX = targetRect.left + targetRect.width / 2;
  const targetCenterY = targetRect.top + targetRect.height / 2;
  const translateX = targetCenterX - sourceCenterX;
  const translateY = targetCenterY - sourceCenterY;
  const scaleX = targetSize / Math.max(sourceRect.width, 1);
  const scaleY = targetSize / Math.max(sourceRect.height, 1);

  return {
    duration,
    transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${scaleX}, ${scaleY})`,
    opacity: '0.4',
    borderRadius: '999px',
  };
}

function start({ root, duration = DEFAULT_DURATION } = {}) {
  if (prefersReducedMotion()) {
    return Promise.resolve(false);
  }

  const sourceImage = getSourceImage(root);
  const target = getAnimationTarget();

  if (!sourceImage || !target) {
    return Promise.resolve(false);
  }

  const sourceRectRaw = sourceImage.getBoundingClientRect();
  const targetRectRaw = target.getBoundingClientRect();
  const overlayHost = getOverlayHost(sourceImage);
  const sourceRect = mapRectToHost(sourceRectRaw, overlayHost);
  const targetRect = mapRectToHost(targetRectRaw, overlayHost);

  if (
    sourceRect.width <= 0 ||
    sourceRect.height <= 0 ||
    targetRect.width <= 0 ||
    targetRect.height <= 0
  ) {
    return Promise.resolve(false);
  }

  cleanupActiveTransition();

  const shell = createAnimationShell(sourceImage, sourceRect, overlayHost);
  const keyframe = buildKeyframe(sourceRect, targetRect, duration);

  let originalOverflow = null;
  if (overlayHost instanceof HTMLDialogElement) {
    originalOverflow = overlayHost.style.overflow;
    overlayHost.style.overflow = 'visible';
  }

  overlayHost.appendChild(shell);

  const cleanup = () => {
    if (overlayHost instanceof HTMLDialogElement) {
      overlayHost.style.overflow = originalOverflow || '';
    }
    shell.remove();
    if (activeCleanup === cleanup) {
      activeCleanup = null;
    }
  };

  activeCleanup = cleanup;

  return runAnimation(shell, keyframe)
    .catch(() => undefined)
    .then(() => {
      cleanup();
      return true;
    });
}

window.CartAddTransition = {
  start,
  cleanup: cleanupActiveTransition,
};
